// ProjectsListPage.tsx — curated mini-roadmap board
//
// Card meta (owner chip + parent crumb + age) sits at the TOP of each card,
// above the title. Owner chip renders capitalized ("Atlas", "Iris").
//
// Each card: meta row + title + mission, then — only when set — the one thing
// this project's agent is on right now ("Now", plain bold text, no frame),
// then the planned next items ("Up next"). No ticket links — tickets live in
// Paperclip; the board is a self-contained roadmap ordered by board_order.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutGrid, List as ListIcon, Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { architectureT as T } from "../blueprint/ArchitectureBlocks";

type ProjectStatus = "active" | "paused" | "done" | "archived";

type ProjectRow = {
  id: string;
  slug: string;
  name: string;
  mission: string | null;
  status: ProjectStatus;
  owner_agent: string | null;
  parent_id: string | null;
  parent_slug: string | null;
  parent_name: string | null;
  current_focus: string | null;
  up_next: string[];
  board_order: number | null;
  goal_count: number;
  created_at: string;
  updated_at: string;
};

const AGENT_COLOR: Record<string, string> = {
  jarvis: "#5B7CBA",
  atlas:  "#A05BBA",
  emma:   "#BA5B8A",
  nova:   "#BA8A5B",
  ben:    "#5BA0BA",
  iris:   "#5BBA7C",
  cleo:   "#5BBA7C",
  kai:    "#7C5BBA",
};

const CSS = `
.pl-page {
  font-family: Inter, sans-serif;
  box-sizing: border-box;
  padding: 24px 14px 64px;
  overflow-x: hidden;
}
.pl-inner { max-width: 1100px; margin: 0 auto; min-width: 0; }
.pl-hero { text-align: center; padding: 8px 4px 16px; }
.pl-eyebrow {
  font-size: 11px; font-weight: 700; letter-spacing: 0.1em;
  text-transform: uppercase; margin-bottom: 10px;
}
.pl-h1 {
  font-size: 26px; font-weight: 800;
  margin: 0 0 10px; letter-spacing: -0.02em; line-height: 1.18;
}
.pl-lede {
  font-size: 14px; line-height: 1.55;
  max-width: 700px; margin: 0 auto;
}
.pl-controls {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; margin: 14px 0 12px;
  flex-wrap: wrap;
}
.pl-stats { font-size: 10px; }
.pl-toggle {
  display: inline-flex; border-radius: 8px; overflow: hidden;
  margin-left: auto;
}
.pl-toggle-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 14px; font-size: 13px; font-weight: 600;
  border: none; cursor: pointer; min-height: 44px;
  font-family: Inter, sans-serif; background: transparent;
  touch-action: manipulation;
}
.pl-grid {
  display: grid; gap: 14px;
  grid-template-columns: 1fr;
  align-items: start;
}
.pl-card {
  display: flex; flex-direction: column; overflow: hidden;
  border-radius: 10px; min-width: 0;
}
.pl-card-meta {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 10px 14px;
  font-size: 10px;
}
.pl-card-meta-time { margin-left: auto; white-space: nowrap; }
.pl-card-body { padding: 14px 16px 12px; min-width: 0; }
.pl-card-title {
  font-size: 18px; font-weight: 700; margin: 0 0 6px;
  letter-spacing: -0.01em; word-break: break-word;
}
.pl-mission {
  font-size: 13px; line-height: 1.5; margin: 0 0 14px;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;
  overflow: hidden; word-break: break-word;
}
.pl-now {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.45;
  display: flex; align-items: baseline; gap: 8px;
  word-break: break-word;
}
.pl-now-label {
  font-size: 10px; font-weight: 800; letter-spacing: 0.12em;
  text-transform: uppercase; flex-shrink: 0;
}
.pl-now-text { font-weight: 700; }
.pl-up-next {
  margin: 0 0 4px;
}
.pl-up-next-label {
  font-size: 10px; font-weight: 700; letter-spacing: 0.1em;
  text-transform: uppercase; margin-bottom: 6px;
}
.pl-up-next-list {
  list-style: none; margin: 0; padding: 0;
}
.pl-up-next-item {
  position: relative;
  padding: 4px 0 4px 14px;
  font-size: 13px;
  line-height: 1.4;
  word-break: break-word;
}
.pl-agent {
  font-size: 10px; font-weight: 700; color: #fff;
  padding: 2px 8px; border-radius: 4px;
  text-transform: capitalize; letter-spacing: 0.03em;
  white-space: nowrap; display: inline-flex; flex-shrink: 0;
}
.pl-list-section { margin-bottom: 28px; min-width: 0; }
.pl-list-meta {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  margin-bottom: 8px;
  font-size: 10px;
}
.pl-list-meta-time { margin-left: auto; white-space: nowrap; }
.pl-list-header {
  margin-bottom: 8px;
}
.pl-list-title {
  font-size: 19px; font-weight: 700; margin: 0;
  letter-spacing: -0.01em; word-break: break-word;
  min-width: 0;
}
.pl-list-mission {
  font-size: 13px; line-height: 1.55;
  margin: 0 0 12px; max-width: 760px;
  word-break: break-word;
}

@media (min-width: 641px) {
  .pl-page { padding: 24px 24px 56px; }
  .pl-inner { max-width: 1400px; }
  .pl-hero { padding: 4px 0 10px; text-align: left; }
  .pl-eyebrow { font-size: 10px; margin-bottom: 6px; }
  .pl-h1 { font-size: 22px; margin: 0 0 6px; }
  .pl-lede { font-size: 13px; line-height: 1.5; max-width: 760px; margin: 0; }
  .pl-controls { margin: 10px 0 12px; }
  .pl-stats { font-size: 11px; }
  .pl-toggle-btn { padding: 5px 10px; font-size: 11px; min-height: 28px; }
  .pl-grid {
    gap: 12px;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  }
  .pl-card-meta { padding: 7px 14px; font-size: 9px; gap: 6px; }
  .pl-card-body { padding: 12px 14px 8px; }
  .pl-card-title { font-size: 15px; margin: 0 0 4px; }
  .pl-mission { font-size: 12px; line-height: 1.45; -webkit-line-clamp: 2; margin-bottom: 10px; }
  .pl-now { font-size: 12px; margin-bottom: 9px; gap: 6px; }
  .pl-now-label { font-size: 9px; }
  .pl-up-next-label { font-size: 9px; margin-bottom: 4px; }
  .pl-up-next-item { font-size: 12px; padding: 2px 0 2px 12px; }
  .pl-agent { font-size: 9px; padding: 1px 6px; }
  .pl-list-section { margin-bottom: 22px; }
  .pl-list-meta { font-size: 9px; gap: 6px; margin-bottom: 6px; }
  .pl-list-title { font-size: 17px; }
  .pl-list-mission { font-size: 12px; line-height: 1.5; margin: 0 0 8px; }
}
@media (min-width: 1024px) {
  .pl-page { padding: 24px 32px 56px; }
}
`;

function OwnerChip({ agent }: { agent: string | null }) {
  if (!agent) return null;
  const color = AGENT_COLOR[agent] ?? T.ink3;
  return (
    <span
      className="pl-agent"
      title={`Owner: ${agent}`}
      style={{ background: color }}
    >{agent}</span>
  );
}

function ParentLink({ p }: { p: ProjectRow }) {
  if (!p.parent_slug || !p.parent_name) return null;
  return (
    <span style={{ color: T.ink3, whiteSpace: "nowrap" }}>
      ↳ <Link
        to={`/projects/${p.parent_slug}`}
        style={{ color: T.ink2, textDecoration: "none" }}
      >{p.parent_name}</Link>
    </span>
  );
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const d = Math.floor(diff / 86400000);
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 7) return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

// "Now" = the one thing the agent is actively on. Plain bold text, no frame.
// Renders nothing when there's no active focus (idle projects show no Now line).
function NowLine({ focus }: { focus: string | null }) {
  if (!focus) return null;
  return (
    <div className="pl-now" style={{ color: T.ink }}>
      <span className="pl-now-label" style={{ color: T.skyDark }}>Now</span>
      <span className="pl-now-text">{focus}</span>
    </div>
  );
}

function UpNext({ items }: { items: string[] }) {
  if (!items.length) {
    return (
      <div className="pl-up-next">
        <div className="pl-up-next-label" style={{ color: T.ink3 }}>Up next</div>
        <div style={{ fontSize: 12, color: T.ink3, fontStyle: "italic" }}>
          Nothing queued.
        </div>
      </div>
    );
  }
  return (
    <div className="pl-up-next">
      <div className="pl-up-next-label" style={{ color: T.ink3 }}>Up next</div>
      <ul className="pl-up-next-list">
        {items.map((s, i) => (
          <li
            key={`${i}-${s}`}
            className="pl-up-next-item"
            style={{ color: T.ink2 }}
          >
            <span aria-hidden style={{ color: T.ink3, marginInlineStart: -10, marginInlineEnd: 4 }}>•</span>
            {s}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CardMeta({ p }: { p: ProjectRow }) {
  return (
    <div
      className="pl-card-meta"
      style={{
        borderBottom: `1px solid ${T.line}`,
        background: T.bg2,
        color: T.ink3,
      }}
    >
      <OwnerChip agent={p.owner_agent} />
      <ParentLink p={p} />
      <span className="pl-card-meta-time">{relativeTime(p.updated_at)}</span>
    </div>
  );
}

function ListMeta({ p }: { p: ProjectRow }) {
  return (
    <div className="pl-list-meta" style={{ color: T.ink3 }}>
      <OwnerChip agent={p.owner_agent} />
      <ParentLink p={p} />
      <span className="pl-list-meta-time">{relativeTime(p.updated_at)}</span>
    </div>
  );
}

function ProjectCard({ p }: { p: ProjectRow }) {
  return (
    <article
      className="pl-card"
      style={{ background: T.white, border: `1px solid ${T.line}` }}
    >
      <CardMeta p={p} />
      <div className="pl-card-body">
        <h2 className="pl-card-title" style={{ color: T.ink }}>
          <Link to={`/projects/${p.slug}`} style={{ color: "inherit", textDecoration: "none" }}>
            {p.name}
          </Link>
        </h2>
        {p.mission && (
          <p className="pl-mission" style={{ color: T.ink2 }}>{p.mission}</p>
        )}
        <NowLine focus={p.current_focus} />
        <UpNext items={p.up_next ?? []} />
      </div>
    </article>
  );
}

function ProjectListItem({ p }: { p: ProjectRow }) {
  return (
    <section className="pl-list-section">
      <ListMeta p={p} />
      <header className="pl-list-header">
        <h2 className="pl-list-title" style={{ color: T.ink }}>
          <Link to={`/projects/${p.slug}`} style={{ color: "inherit", textDecoration: "none" }}>
            {p.name}
          </Link>
        </h2>
      </header>
      {p.mission && (
        <p className="pl-list-mission" style={{ color: T.ink2 }}>{p.mission}</p>
      )}
      <div style={{
        border: `1px solid ${T.line}`, borderRadius: 8,
        background: T.white, overflow: "hidden",
        padding: "14px 16px",
      }}>
        <NowLine focus={p.current_focus} />
        <UpNext items={p.up_next ?? []} />
      </div>
    </section>
  );
}

function ViewToggle({ view, setView }: { view: "grid" | "list"; setView: (v: "grid" | "list") => void }) {
  const btn = (mode: "grid" | "list", icon: React.ReactNode, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={view === mode}
      onClick={() => setView(mode)}
      className="pl-toggle-btn"
      style={{
        background: view === mode ? T.skySoft : "transparent",
        color: view === mode ? T.skyDark : T.ink2,
        borderLeft: mode === "list" ? `1px solid ${T.line}` : undefined,
      }}
    >
      {icon}
      {label}
    </button>
  );
  return (
    <div
      role="tablist"
      aria-label="View"
      className="pl-toggle"
      style={{ border: `1px solid ${T.line}`, background: T.white }}
    >
      {btn("grid", <LayoutGrid style={{ width: 14, height: 14 }} />, "Grid")}
      {btn("list", <ListIcon style={{ width: 14, height: 14 }} />, "List")}
    </div>
  );
}

export function ProjectsListPage() {
  const api = useApi();
  const [rows, setRows] = useState<ProjectRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<"grid" | "list">("grid");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/api/projects");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as ProjectRow[];
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const projects = (rows ?? []).filter((p) => p.status === "active");
  const totalUpNext = projects.reduce((n, p) => n + (p.up_next?.length ?? 0), 0);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div className="pl-page">
        <div className="pl-inner">
          <div className="pl-hero">
            <div className="pl-eyebrow" style={{ color: T.skyDark }}>
              MyJarvis · Portfolio
            </div>
            <h1 className="pl-h1" style={{ color: T.ink }}>Projects</h1>
            <p className="pl-lede" style={{ color: T.ink2 }}>
              Active projects and what's moving on each. Curated by Jarvis + Erez — tickets are managed in Paperclip.
            </p>
          </div>

          <div className="pl-controls">
            {rows && (
              <span className="pl-stats" style={{ color: T.ink3 }}>
                {projects.length} active · {totalUpNext} queued
              </span>
            )}
            <ViewToggle view={view} setView={setView} />
          </div>

          {error ? (
            <div style={{
              padding: "14px 16px", color: T.red, background: T.redSoft,
              border: `1px solid ${T.red}`, borderRadius: 8, fontSize: 13,
            }}>Failed to load: {error}</div>
          ) : rows === null ? (
            <div style={{
              padding: 20, fontSize: 14, color: T.ink3,
              display: "inline-flex", alignItems: "center", gap: 8,
            }}>
              <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />
              Loading…
            </div>
          ) : projects.length === 0 ? (
            <div style={{ padding: 20, color: T.ink3, fontSize: 14 }}>
              No active projects.
            </div>
          ) : view === "grid" ? (
            <div className="pl-grid">
              {projects.map((p) => <ProjectCard key={p.id} p={p} />)}
            </div>
          ) : (
            <div>
              {projects.map((p) => <ProjectListItem key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

(ProjectsListPage as unknown as { path: string }).path = "/projects-list";
